import React, { useState, useRef, useMemo, useCallback } from "react";

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
  "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
  "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
  "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
  "New Hampshire", "New Jersey", "New Mexico", "New York",
  "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
  "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
  "West Virginia", "Wisconsin", "Wyoming"
];

// Memoized option component to prevent unnecessary re-renders
const AutocompleteOption = React.memo(({ option, onSelect, searchTerm }) => {
  const handleMouseDown = useCallback(() => {
    onSelect(option);
  }, [option, onSelect]);

  // Highlight matching text for better UX
  const highlightedText = useMemo(() => {
    if (!searchTerm) return option;
    
    const index = option.toLowerCase().indexOf(searchTerm.toLowerCase());
    if (index === -1) return option;
    
    return (
      <>
        {option.slice(0, index)}
        <strong>{option.slice(index, index + searchTerm.length)}</strong>
        {option.slice(index + searchTerm.length)}
      </>
    );
  }, [option, searchTerm]);

  return (
    <li
      onMouseDown={handleMouseDown}
      className="autocomplete-option"
      role="option"
      aria-selected="false"
    >
      {highlightedText}
    </li>
  );
});

AutocompleteOption.displayName = 'AutocompleteOption';

const Autocomplete = ({inputValue , setInputValue}) => {
  // const [inputValue, setInputValue] = useState("");
  const [showOptions, setShowOptions] = useState(false);
  const inputRef = useRef(null);

  // Create a map for O(1) lookup instead of O(n) search
  const stateMap = useMemo(() => {
    const map = new Map();
    US_STATES.forEach(state => {
      map.set(state.toLowerCase(), state);
    });
    return map;
  }, []);

  // Memoize filtered options with debouncing effect through useMemo
  const filteredOptions = useMemo(() => {
    if (inputValue.trim() === "") {
      return [];
    }
    
    const searchTerm = inputValue.toLowerCase();
    
    // More efficient filtering - limit results and use early termination
    const results = [];
    const maxResults = 10; // Limit dropdown size for better performance
    
    for (const state of US_STATES) {
      if (state.toLowerCase().includes(searchTerm)) {
        results.push(state);
        if (results.length >= maxResults) break;
      }
    }
    
    return results;
  }, [inputValue]);

  // Memoize event handlers to prevent unnecessary re-renders
  const handleSelect = useCallback((value) => {
    setInputValue(value);
    setShowOptions(false);
  }, [setInputValue]);

  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setInputValue(value);
    setShowOptions(true);
  }, [setInputValue]);

  const handleFocus = useCallback(() => {
    setShowOptions(true);
  }, []);

  const handleBlur = useCallback(() => {
    // Use the more efficient map lookup
    if (!stateMap.has(inputValue.toLowerCase())) {
      setInputValue("");
    }
    setShowOptions(false);
  }, [inputValue, stateMap, setInputValue]);

  // Add keyboard navigation for better accessibility
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setShowOptions(false);
      inputRef.current?.blur();
    }
  }, []);

  const shouldShowDropdown = showOptions && filteredOptions.length > 0;

  return (
    <div className="autocomplete-container" style={containerStyles}>
      <input
        ref={inputRef}
        type="text"
        placeholder="Type to search US states..."
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="autocomplete-input"
        style={inputStyles}
        autoComplete="off"
        role="combobox"
        aria-expanded={shouldShowDropdown}
        aria-haspopup="listbox"
        aria-controls="autocomplete-listbox"
      />
      {shouldShowDropdown && (
        <ul 
          className="autocomplete-dropdown" 
          style={dropdownStyles}
          role="listbox"
          id="autocomplete-listbox"
        >
          {filteredOptions.map((option) => (
            <AutocompleteOption
              key={option}
              option={option}
              onSelect={handleSelect}
              searchTerm={inputValue}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

// Inline styles to avoid CSS dependency
const containerStyles = {
  position: 'relative',
  width: '150px',
  margin: 'auto 0px auto 10px'
};

const inputStyles = {
  width: '100%',
  padding: '5px',
  border: '2px solid #e1e5e9',
  borderRadius: '20px',
  fontSize: '16px',
  outline: 'none',
  transition: 'border-color 0.2s ease',
  boxSizing: 'border-box'
};

const dropdownStyles = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  backgroundColor: 'white',
  border: '1px solid #e1e5e9',
  borderTop: 'none',
  borderRadius: '0 0 8px 8px',
  maxHeight: '200px',
  overflowY: 'auto',
  zIndex: 1000,
  margin: 0,
  padding: 0,
  listStyle: 'none',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
};

// Add CSS-in-JS for option styles
const optionStyles = `
.autocomplete-option {
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.15s ease;
  color: black;
}

.autocomplete-option:hover {
  background-color: #f8f9fa;
}

.autocomplete-option:last-child {
  border-bottom: none;
}

.autocomplete-option strong {
  color: #007bff;
  font-weight: 600;
}
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = optionStyles;
  document.head.appendChild(styleSheet);
}

export default Autocomplete;